import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { validateEmail } from 'utils/validators';
import { useTextareaEffects } from 'hooks/useTextareaEffects';
import { useLazyGetVehicleContactsQuery } from 'services/api/transfer-report';
import { useSendReportOfSaleMutation, RosReportType } from 'services/api/report-of-sale';
import { modalsToggle } from 'actions';
import ModalWrapper from 'components/ModalWrapper';

const MODAL_SUCCESS = 'ros-send-success';
const MODAL_DUPLICATE = 'ros-send-duplicate';

type RecipientType = 'buyer' | 'seller' | 'other';

interface SendReportOfSaleModalProps {
  vehicleId: number;
  reportType: RosReportType;
  versionId: number;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  z-index: 1500;
  overflow-y: auto;
`;

const Dialog = styled.div`
  background: #fff;
  width: 560px;
  max-width: 100%;
  max-height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Container = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
`;

const SendNoteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: #000;
`;

const NoteLabel = styled.label`
  font-size: 18px;
  font-weight: 400;
  color: #949494;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 14px;
  color: #000;
  resize: none;
  overflow: hidden;
  box-sizing: border-box;
  font-family: inherit;
  outline: none;

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }

  &:focus {
    border: 1px solid #005a96;
  }
`;

const RecipientsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RecipientsCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RecipientLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #000;
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CircleMark = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid ${({ $checked }) => ($checked ? '#005a96' : '#ccc')};
  background: ${({ $checked }) => ($checked ? '#005a96' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
`;

const SquareMark = styled.div<{ $checked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid ${({ $checked }) => ($checked ? '#005a96' : '#ccc')};
  background: ${({ $checked }) => ($checked ? '#005a96' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
`;

const Checkmark = styled.div`
  width: 8px;
  height: 4px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translateY(-1px);
`;

const EmailWrapper = styled.div<{ $error?: boolean }>`
  background: #f5f5f5;
  border-radius: 4px;
  padding: 4px 12px;
  border: 1px solid ${({ $error }) => ($error ? '#d32f2f' : 'transparent')};
`;

const EmailInput = styled.input`
  width: 100%;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #000;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #d32f2f;
  margin-top: 2px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const ContactItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #000;
  cursor: pointer;
  user-select: none;
`;

const AddLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #005a96;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  align-self: flex-start;
  margin-top: 4px;

  &:hover {
    color: #004a80;
  }
`;

const SendModalGlobalStyles = createGlobalStyle`
  .send-modal-actions {
    padding: 12px 32px 24px !important;
    gap: 12px;
    button {
      flex: 1 !important;
      min-width: 0 !important;
      margin: 0 !important;
    }
  }
`;

const SEND_MODAL_PAPER_PROPS = { style: { minWidth: 520 } };

const Footer = styled.div`
  padding: 16px 24px;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  height: 44px;
  padding: 0 24px;
  background: transparent;
  border: 1px solid #005a96;
  color: #005a96;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
  width: 120px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #f0f6fc;
  }
`;

const SendButton = styled.button`
  height: 44px;
  width: 120px;
  padding: 0 24px;
  background: #005a96;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #004a80;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// NoteTextarea wrapper (needs ref for auto-resize)
// ---------------------------------------------------------------------------

const NoteField = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const textareaRef = useTextareaEffects(value);
  return (
    <>
      <NoteLabel>Note</NoteLabel>
      <NoteTextarea
        ref={textareaRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        placeholder="Write your message here..."
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RECIPIENT_OPTIONS: { value: RecipientType; label: string }[] = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'other', label: 'Other' },
];

function parseEmails(raw: string): string[] {
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function validateEmails(emails: string[]): string | null {
  if (emails.length === 0) return 'At least one email is required';
  const invalid = emails.find(e => validateEmail(e) !== undefined);
  if (invalid) return `"${invalid}" is not a valid email`;
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SendReportOfSaleModal = ({ vehicleId, reportType, versionId, onClose }: SendReportOfSaleModalProps) => {
  const [sendReport, { isLoading: isSending }] = useSendReportOfSaleMutation();
  const [fetchContacts, { data, isFetching: isLoadingContacts }] = useLazyGetVehicleContactsQuery();
  const contacts = data?.emails || [];

  // Recipient state
  const [recipientType, setRecipientType] = useState<RecipientType | null>(null);
  const [selectedContactEmails, setSelectedContactEmails] = useState<Set<string>>(new Set());
  const [showAddEmailInput, setShowAddEmailInput] = useState(false);
  const [addEmailInput, setAddEmailInput] = useState('');
  const [addEmailError, setAddEmailError] = useState<string | null>(null);
  const [otherEmailInput, setOtherEmailInput] = useState('');
  const [otherEmailError, setOtherEmailError] = useState<string | null>(null);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const [pendingEmails, setPendingEmails] = useState<string[] | null>(null);

  // Tracks emails sent this session
  const [sentEmails, setSentEmails] = useState<Set<string>>(new Set());

  // Note
  const [note, setNote] = useState('');

  // Preselect first contact when contacts load
  useEffect(() => {
    if (contacts.length > 0) {
      setSelectedContactEmails(new Set([contacts[0]]));
    }
  }, [contacts]);

  // ---------------------------------------------------------------------------
  // Recipient type toggle
  // ---------------------------------------------------------------------------

  const resetRecipientInputs = () => {
    setSelectedContactEmails(new Set());
    setShowAddEmailInput(false);
    setAddEmailInput('');
    setAddEmailError(null);
    setOtherEmailInput('');
    setOtherEmailError(null);
  };

  const handleRecipientTypeToggle = (type: RecipientType) => {
    setRecipientError(null);
    if (recipientType === type) {
      setRecipientType(null);
      resetRecipientInputs();
      return;
    }
    setRecipientType(type);
    resetRecipientInputs();

    if (type === 'buyer' || type === 'seller') {
      fetchContacts({ vehicleId, userType: type });
    }
  };

  const toggleContactEmail = (email: string) => {
    setSelectedContactEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
    setRecipientError(null);
  };

  // ---------------------------------------------------------------------------
  // Email collection
  // ---------------------------------------------------------------------------

  const collectEmails = (): string[] => {
    if (recipientType === 'other') {
      return parseEmails(otherEmailInput);
    }
    const contactEmails = Array.from(selectedContactEmails);
    const added = showAddEmailInput ? parseEmails(addEmailInput) : [];
    return [...contactEmails, ...added];
  };

  // ---------------------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------------------

  const doSend = async (emails: string[]) => {
    await sendReport({ vehicleId, reportType, to: emails, notes: note, versionId }).unwrap();
    setSentEmails(prev => new Set([...prev, ...emails]));
    dispatch(modalsToggle(MODAL_SUCCESS));
  };

  const handleSend = async () => {
    setOtherEmailError(null);
    setAddEmailError(null);
    setRecipientError(null);

    if (!recipientType) {
      setRecipientError('Select at least one recipient');
      return;
    }

    const emails = collectEmails();

    if (recipientType === 'other') {
      const err = validateEmails(emails);
      if (err) {
        setOtherEmailError(err);
        return;
      }
    } else {
      if (showAddEmailInput && addEmailInput.trim()) {
        const addedEmails = parseEmails(addEmailInput);
        const err = validateEmails(addedEmails);
        if (err) {
          setAddEmailError(err);
          return;
        }
      }
      if (emails.length === 0) {
        setRecipientError('Select at least one contact or add an email');
        return;
      }
    }

    const hasDuplicate = emails.some(e => sentEmails.has(e));
    if (hasDuplicate) {
      setPendingEmails(emails);
      dispatch(modalsToggle(MODAL_DUPLICATE));
      return;
    }

    await doSend(emails);
  };

  const handleConfirmSendAnyway = async () => {
    if (!pendingEmails) return;
    dispatch(modalsToggle(MODAL_DUPLICATE));
    await doSend(pendingEmails);
    setPendingEmails(null);
  };

  const handleCancelDuplicate = () => {
    dispatch(modalsToggle(MODAL_DUPLICATE));
    setPendingEmails(null);
  };

  const handleFinish = () => {
    dispatch(modalsToggle(MODAL_SUCCESS));
    onClose();
  };

  const handleSendToMore = () => {
    dispatch(modalsToggle(MODAL_SUCCESS));
    setRecipientType(null);
    resetRecipientInputs();
    setRecipientError(null);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return createPortal(
    <Overlay onClick={onClose}>
      <SendModalGlobalStyles />
      <ModalWrapper
        modalId={MODAL_DUPLICATE}
        title="Already Sent"
        submitText="Send Anyway"
        handleSubmit={handleConfirmSendAnyway}
        submitting={isSending}
        cancelText="Cancel"
        cancelCustomAction={handleCancelDuplicate}
        actionsClassName="send-modal-actions"
        dialogProps={{ PaperProps: SEND_MODAL_PAPER_PROPS }}
      >
        You have already sent this report to the selected recipient(s). Are you sure you want to send it again?
      </ModalWrapper>
      <ModalWrapper
        modalId={MODAL_SUCCESS}
        title="Report Sent"
        submitText="Finish"
        handleSubmit={handleFinish}
        cancelText="Send to more recipients"
        cancelCustomAction={handleSendToMore}
        cancelPosition="left"
        actionsClassName="send-modal-actions"
        dialogProps={{ PaperProps: SEND_MODAL_PAPER_PROPS }}
      >
        Report sent successfully.
      </ModalWrapper>

      <Dialog onClick={e => e.stopPropagation()}>
        <Container>
          <SendNoteSection>
            <SectionTitle>Send Report of Sale</SectionTitle>

            <NoteField value={note} onChange={setNote} />

            {/* Top-level recipient type selection */}
            <RecipientsRow>
              <RecipientsCheckboxRow>
                {RECIPIENT_OPTIONS.map(({ value, label }) => (
                  <RecipientLabel key={value}>
                    <HiddenCheckbox
                      type="checkbox"
                      checked={recipientType === value}
                      onChange={() => handleRecipientTypeToggle(value)}
                    />
                    <CircleMark $checked={recipientType === value}>
                      {recipientType === value && <Checkmark />}
                    </CircleMark>
                    {label}
                  </RecipientLabel>
                ))}
              </RecipientsCheckboxRow>
              {recipientError && <FieldError>{recipientError}</FieldError>}
            </RecipientsRow>

            {/* Other: comma-separated email input */}
            {recipientType === 'other' && (
              <FieldWrapper>
                <EmailWrapper $error={!!otherEmailError}>
                  <EmailInput
                    type="text"
                    placeholder="Emails (comma separated)"
                    value={otherEmailInput}
                    onChange={e => {
                      setOtherEmailInput(e.target.value);
                      setOtherEmailError(null);
                    }}
                  />
                </EmailWrapper>
                {otherEmailError && <FieldError>{otherEmailError}</FieldError>}
              </FieldWrapper>
            )}

            {/* Buyer / Seller: contact list */}
            {(recipientType === 'buyer' || recipientType === 'seller') && (
              <>
                {isLoadingContacts ? (
                  <span style={{ fontSize: 14, color: '#949494' }}>Loading contacts…</span>
                ) : (
                  <ContactList>
                    {contacts.map(email => (
                      <ContactItem key={email}>
                        <HiddenCheckbox
                          type="checkbox"
                          checked={selectedContactEmails.has(email)}
                          onChange={() => toggleContactEmail(email)}
                        />
                        <SquareMark $checked={selectedContactEmails.has(email)}>
                          {selectedContactEmails.has(email) && <Checkmark />}
                        </SquareMark>
                        {email}
                      </ContactItem>
                    ))}
                  </ContactList>
                )}

                {!showAddEmailInput && (
                  <AddLink type="button" onClick={() => setShowAddEmailInput(true)}>
                    Add
                  </AddLink>
                )}

                {showAddEmailInput && (
                  <FieldWrapper>
                    <EmailWrapper $error={!!addEmailError}>
                      <EmailInput
                        type="text"
                        placeholder="Emails (comma separated)"
                        value={addEmailInput}
                        onChange={e => {
                          setAddEmailInput(e.target.value);
                          setAddEmailError(null);
                        }}
                      />
                    </EmailWrapper>
                    {addEmailError && <FieldError>{addEmailError}</FieldError>}
                  </FieldWrapper>
                )}
              </>
            )}
          </SendNoteSection>
        </Container>

        <Footer>
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
          <SendButton type="button" disabled={isSending} onClick={handleSend}>
            Send
          </SendButton>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default SendReportOfSaleModal;
